import React, { useState, useEffect } from 'react';
import { TextField, Checkbox, FlexBox, Alert } from '@cimpress/react-components';
import EscapableModal from '../EscapableModal';
import CancelButton from '../../../generic/buttons/CancelButton';
import ConfirmButton from '../../../generic/buttons/ConfirmButton';

function initState(crawlingOptions) {
  const opts = crawlingOptions || {};
  return {
    curie: opts.curie || '',
    acceptPreference: opts.acceptPreference || '',
    maxCrawlDepth: opts.maxCrawlDepth != null ? String(opts.maxCrawlDepth) : '',
    linkTimeout: opts.linkTimeout != null ? String(opts.linkTimeout) : '',
    whitelist: Array.isArray(opts.whitelist) ? opts.whitelist.join(',') : '',
    blacklist: Array.isArray(opts.blacklist) ? opts.blacklist.join(',') : '',
    crawlerSoftErrors: Array.isArray(opts.crawlerSoftErrors) ? opts.crawlerSoftErrors.join(',') : '',
    disableRateLimiting: !!opts.disableRateLimiting,
    autoDetectContentType: !!opts.autoDetectContentType
  };
}

function buildResult(state) {
  const result = {};

  if (state.curie.trim()) {
    result.curie = state.curie.trim();
  }
  if (state.acceptPreference.trim()) {
    result.acceptPreference = state.acceptPreference.trim();
  }
  if (state.maxCrawlDepth !== '') {
    result.maxCrawlDepth = parseInt(state.maxCrawlDepth, 10);
  }
  if (state.linkTimeout !== '') {
    result.linkTimeout = parseInt(state.linkTimeout, 10);
  }
  if (state.whitelist.trim()) {
    result.whitelist = state.whitelist.split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  if (state.blacklist.trim()) {
    result.blacklist = state.blacklist.split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  if (state.crawlerSoftErrors.trim()) {
    result.crawlerSoftErrors = state.crawlerSoftErrors.split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }
  if (state.disableRateLimiting) {
    result.disableRateLimiting = true;
  }
  if (state.autoDetectContentType) {
    result.autoDetectContentType = true;
  }

  return Object.keys(result).length > 0 ? result : undefined;
}

function CrawlingOptionsModal({ show, t, crawlingOptions, onConfirm, onClose }) {
  const [state, setState] = useState(() => initState(crawlingOptions));

  useEffect(() => {
    if (show) {
      setState(initState(crawlingOptions));
    }
  }, [show, crawlingOptions]);

  const update = (field, value) => {
    setState(prev => ({ ...prev, [field]: value }));
  };

  const handleConfirm = () => {
    onConfirm(buildResult(state));
  };

  const title = <h4>{t('toolkit.crawlingOptionsModalTitle')}</h4>;

  const footer = <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
    <CancelButton onClick={onClose} />
    <ConfirmButton autoFocus onClick={handleConfirm} />
  </div>;

  const body = <div style={{ padding: '10px 15px' }}>
    <Alert status='info' message={t('toolkit.crawlingOptionsInfo')} />
    <FlexBox spaceBetween marginX='s'>
      <TextField
        style={{ flexGrow: 1, marginBottom: '5px' }}
        label={t('toolkit.crawlingOptions.whitelist')}
        value={state.whitelist}
        onChange={(e) => update('whitelist', e.target.value)}
      />
      <TextField
        style={{ flexGrow: 1, marginBottom: '5px' }}
        label={t('toolkit.crawlingOptions.blacklist')}
        value={state.blacklist}
        onChange={(e) => update('blacklist', e.target.value)}
      />
    </FlexBox>
    <FlexBox spaceBetween marginX='s'>
      <TextField
        style={{ flexGrow: 1, marginBottom: '5px' }}
        label={t('toolkit.crawlingOptions.curie')}
        value={state.curie}
        onChange={(e) => update('curie', e.target.value)}
      />
      <TextField
        style={{ flexGrow: 1, marginBottom: '5px' }}
        label={t('toolkit.crawlingOptions.acceptPreference')}
        value={state.acceptPreference}
        onChange={(e) => update('acceptPreference', e.target.value)}
      />
    </FlexBox>
    <FlexBox spaceBetween marginX='s'>
      <TextField
        type='number'
        style={{ flexGrow: 1, marginBottom: '5px' }}
        label={t('toolkit.crawlingOptions.maxCrawlDepth')}
        value={state.maxCrawlDepth}
        onChange={(e) => update('maxCrawlDepth', e.target.value)}
      />
      <TextField
        type='number'
        style={{ flexGrow: 1, marginBottom: '5px' }}
        label={t('toolkit.crawlingOptions.linkTimeout')}
        value={state.linkTimeout}
        onChange={(e) => update('linkTimeout', e.target.value)}
      />
    </FlexBox>
    <FlexBox spaceBetween marginX='s'>
      <TextField
        style={{ flexGrow: 1, marginBottom: '5px' }}
        label={t('toolkit.crawlingOptions.crawlerSoftErrors')}
        value={state.crawlerSoftErrors}
        onChange={(e) => update('crawlerSoftErrors', e.target.value)}
      />
    </FlexBox>
    <div style={{ marginTop: '10px' }}>
      <Checkbox
        label={t('toolkit.crawlingOptions.disableRateLimiting')}
        checked={state.disableRateLimiting}
        onChange={() => update('disableRateLimiting', !state.disableRateLimiting)}
      />
      <Checkbox
        label={t('toolkit.crawlingOptions.autoDetectContentType')}
        checked={state.autoDetectContentType}
        onChange={() => update('autoDetectContentType', !state.autoDetectContentType)}
      />
    </div>
  </div>;

  return (
    <EscapableModal
      show={show}
      style={{ width: '70%' }}
      title={title}
      footer={footer}
      closeOnOutsideClick
      closeButton={false}
      onRequestHide={onClose}
    >
      {body}
    </EscapableModal>
  );
}

export default CrawlingOptionsModal;
